import { SNAP_LANGUAGE_PREFERENCE } from "../constants";

/**
 * FOC-248: GA4 funnel events for the visitor journey around scanning:
 * home → scan → result → recap email sign-up.
 *
 * Recognition itself (match rate, latency, frames per burst) is measured by
 * the engine per build, so it's deliberately NOT duplicated here. Every event
 * carries build_id (same "<stage>-<sha>" as the engine's scan records) and the
 * visitor's language, so GA4 can compare the funnel before/after a release.
 *
 * Events go through the page's existing gtag (public/index.html), which already
 * reaches the GA4 property. Analytics must never break the app: no gtag (ad
 * blocker, offline) → silent no-op.
 */
export const BUILD_ID = process.env.REACT_APP_FOCUS_BUILD_ID || "dev-local";

export type FunnelEvent =
  | "home_view" // Home rendered ("Are you at the Barnes?")
  | "home_away" // visitor said they're not at the Barnes
  | "scan_start" // tapped start scanning
  | "camera_permission" // camera granted/denied after scan_start
  | "scan_attempt" // a scan began on /scan (first or retry)
  | "scan_no_match" // no-match screen shown
  | "result_view" // artwork/exhibition result page shown
  | "email_form_open" // visitor opened the recap email panel
  | "recap_email_submit" // visitor submitted their email for a recap
  | "camera_preview_error"; // replaces the legacy `scanner` event

const gtagFn = (): ((...args: any[]) => void) | null => {
  const g = (window as any).gtag;
  return typeof g === "function" ? g : null;
};

const currentLanguage = (): string => {
  try {
    return localStorage.getItem(SNAP_LANGUAGE_PREFERENCE) || "En";
  } catch {
    return "En";
  }
};

let userPropsSet = false;

export const track = (
  name: FunnelEvent,
  params: Record<string, string | number | boolean | undefined> = {}
): void => {
  try {
    const gtag = gtagFn();
    if (!gtag) return;
    const language = currentLanguage();
    if (!userPropsSet) {
      gtag("set", "user_properties", { focus_build: BUILD_ID, language });
      userPropsSet = true;
    }
    gtag("event", name, { build_id: BUILD_ID, language, ...params });
  } catch {
    // never let analytics break the visitor flow
  }
};

/** Visitor changed language: keep the user property current. */
export const setAnalyticsLanguage = (language: string): void => {
  try {
    gtagFn()?.("set", "user_properties", { language });
  } catch {
    // ignore
  }
};

/** Fire an event at most once per page load (for error handlers that can repeat every frame). */
const fired = new Set<string>();
export const trackOnce = (
  name: FunnelEvent,
  params?: Record<string, string | number | boolean | undefined>
): void => {
  if (fired.has(name)) return;
  fired.add(name);
  track(name, params);
};
